import React, { useState, useEffect } from 'react';
import { styled, Grid, Box, Divider, Chip, Stack } from '@mui/material';
import {
    IconButton,
    Typography,
    TextField as _TextField,
    InputAdornment,
    FormControlLabel,
    Switch,
} from '@mui/material';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { RadioGroup, Radio } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Alert } from '@mui/material';

// Components
import { TextArea, Dropdown, DatePicker, TimePicker } from 'components/ui/inputs';
import { Section, Header, Content } from 'components/ui/forms';
import { DeleteButton } from 'components/ui/buttons';
import { SubmitButton } from 'components/ui/buttons';
import { CancelButton } from 'components/ui/buttons';
import ReceiptPreview from 'components/ReceiptPreview';
import Tooltip from 'components/popups/Tooltip';

// Utility
import moment from 'moment-timezone';
import { useNotification } from 'context/notification';
import { useTextInput, useNumberInput, useDatePicker } from 'hooks/forms';
import { formatPrice } from 'utility/numbers';
import { getMomentFromDateAndTime } from 'utility/validation';

export default function BundleForm(props) {
    const {
        editMode,
        deletable,
        products,
        eventId,
        hostId,
        productGroups = [],
        onSubmit,
        onCancel,
        onDelete,
        loading,
    } = props;
    const d = props.defaultValues;

    // Cart State
    const [cart, setCart] = useState(products);
    const [emptyCart, setEmptyCart] = useState(false);

    // Form State
    const name = useTextInput({ defaultValue: d.name, maxLength: 100 });
    const description = useTextInput({ defaultValue: d.description, maxLength: 1000 });
    const productGroupId = useTextInput({
        defaultValue: d.productGroupId ? String(d.productGroupId) : '',
    });
    const price = useNumberInput({ defaultValue: d.price, format: 'currency' });
    const qtyAvailable = useNumberInput({ defaultValue: d.qtyAvailable, integerOnly: true });
    const qtyMin = useNumberInput({ defaultValue: d.qtyMin, integerOnly: true });
    const qtyMax = useNumberInput({ defaultValue: d.qtyMax, integerOnly: true });
    const saleStartDate = useDatePicker({ defaultValue: d.saleStartDate });
    const saleStartTime = useDatePicker({ defaultValue: d.saleStartDate });
    const saleEndDate = useDatePicker({ defaultValue: d.saleEndDate });
    const saleEndTime = useDatePicker({ defaultValue: d.saleEndDate });
    const accessCode = useTextInput({ defaultValue: d.accessCode, noSpaces: true, maxLength: 50 });
    const [audience, setAudience] = useState(d.audience); // visible, hidden, accessCode
    const [displayDiscountFlag, setDisplayDiscountFlag] = useState(Boolean(d.displayDiscountFlag));

    // Error States
    const [negativeSalePeriod, setNegativeSalePeriod] = useState(false);

    // Warnings: Alerts the user of potential issues, but allow them to save the bundle if they accept the warnings
    const [warnings, setWarnings] = useState({
        insufficientProducts: false,
        negativeSavings: false,
    });

    // If negativeSalePeriod is true, check on every render if the user fixed the issue, then hide the error message
    useEffect(() => {
        if (negativeSalePeriod === true) {
            if (
                saleStartDate.value.isValid() &&
                saleStartTime.value.isValid() &&
                saleEndDate.value.isValid() &&
                saleEndTime.value.isValid()
            ) {
                if (
                    moment(
                        saleStartDate.value.format('YYYY-MM-DD') +
                            ' ' +
                            saleStartTime.value.format('HH:mm:ss'),
                    ) <
                    moment(
                        saleEndDate.value.format('YYYY-MM-DD') +
                            ' ' +
                            saleEndTime.value.format('HH:mm:ss'),
                    )
                ) {
                    setNegativeSalePeriod(false);
                }
            }
        }
    }, [negativeSalePeriod, saleStartDate, saleStartTime, saleEndDate, saleEndTime]);

    const { notify } = useNotification();

    // Calculate savings in $ and %
    let totalValue = 0;
    let ticketCount = 0;

    for (const p of cart) {
        totalValue = Number((totalValue + Number((p.price * p.quantity).toFixed(2))).toFixed(2));
        ticketCount += p.quantity;
    }

    let savingAmount = 0;
    let savingPercent = 0;

    if (totalValue > 0) {
        savingAmount = Number((totalValue - Number(price.value)).toFixed(2));
        savingPercent = Number(((savingAmount / totalValue).toFixed(2) * 100).toFixed(2));
    }

    const handleSubmit = (ignoreWarnings) => {
        let formError = false;

        const bundleProducts = cart
            .filter((p) => p.quantity > 0)
            .map((p) => ({ id: p.id, qty: p.quantity }));

        if (bundleProducts.length === 0) {
            setEmptyCart(true);
            formError = true;
        } else {
            setEmptyCart(false);
        }

        if (name.value.trim() === '') {
            name.setError('This field cannot be blank');
            formError = true;
        }

        if (!price.value) {
            price.setError('This field cannot be blank');
            formError = true;
        }

        if (!qtyAvailable.value) {
            qtyAvailable.setError('This field cannot be blank');
            formError = true;
        }

        if (!qtyMin.value) {
            qtyMin.setError('This field cannot be blank');
            formError = true;
        }

        if (!qtyMax.value) {
            qtyMax.setError('This field cannot be blank');
            formError = true;
        }

        // Check for invalid dates
        if (
            !saleStartDate.value.isValid() ||
            !saleStartTime.value.isValid() ||
            !saleEndDate.value.isValid() ||
            !saleEndTime.value.isValid()
        ) {
            formError = true;
        }
        // Check that end date is after start date
        else if (
            moment(
                saleStartDate.value.format('YYYY-MM-DD') +
                    ' ' +
                    saleStartTime.value.format('HH:mm:ss'),
            ) >
            moment(
                saleEndDate.value.format('YYYY-MM-DD') + ' ' + saleEndTime.value.format('HH:mm:ss'),
            )
        ) {
            formError = true;
            setNegativeSalePeriod(true);
        } else {
            setNegativeSalePeriod(false);
        }

        // Check for invalid access code (if feature is enabled)
        if (audience === 'accessCode' && accessCode.value === '') {
            accessCode.setError('This field cannot be blank');
            formError = true;
        }

        if (formError) {
            notify.warning('Please ensure all fields are filled out correctly');
            return;
        }

        // Check for potential issues with the bundle, and warn user.
        // If they accepted the warning, ignoreWarnings will be true, and this section is skipped.
        if (ignoreWarnings !== true) {
            let issueWarning = false;
            let insufficientProducts = false;
            let negativeSavings = false;

            // Ensure price is not higher than total value
            if (price.value > totalValue) {
                negativeSavings = true;
                issueWarning = true;
            }

            // Ensure enough products are available
            const productsWithoutEnoughStock = bundleProducts.filter((p) => {
                const product = products.find((i) => i.id === p.id);
                return product.total - product.sold < p.qty;
            });

            // If there are insufficient products, show warning. User may still submit form after accepting warning.
            if (productsWithoutEnoughStock.length > 0) {
                insufficientProducts = true;
                issueWarning = true;
            }

            if (issueWarning) {
                setWarnings((w) => ({ ...w, insufficientProducts, negativeSavings }));
                return;
            }
        }

        const bundleInfo = {
            name: name.value.trim(),
            description: description.value.trim() || null,
            productGroupId: productGroupId.value ? Number(productGroupId.value) : null,
            price: Number(price.value),
            qtyAvailable: Number(qtyAvailable.value),
            qtyMin: Number(qtyMin.value),
            qtyMax: Number(qtyMax.value),
            saleStartDate:
                saleStartDate.value.format('YYYY-MM-DD') +
                ' ' +
                saleStartTime.value.format('HH:mm:00'),
            saleEndDate:
                saleEndDate.value.format('YYYY-MM-DD') + ' ' + saleEndTime.value.format('HH:mm:59'),
            visible: audience === 'visible' || audience === 'accessCode',
            accessCode: accessCode.value || null, // accessCode input does not allow spaces
            displayDiscountFlag: displayDiscountFlag,
        };

        onSubmit(bundleInfo, bundleProducts);
    };

    const handleProductUpdate = (id, value) => {
        const newValue = Number(value);

        if (Number.isInteger(newValue) && newValue >= 0 && newValue <= 99) {
            setCart(
                cart.map((i) => {
                    if (i.id === id) {
                        return { ...i, quantity: newValue };
                    } else {
                        return i;
                    }
                }),
            );
        }
    };

    const handleAudienceChange = (e) => {
        const audience = e.target.value;

        // Clear access code value
        // Note: calling setValue() also sets the error state to false
        if (audience !== 'accessCode') {
            accessCode.setValue('');
        }

        setAudience(audience);
    };

    const handleWarningAccept = () => {
        setWarnings((w) => ({ ...w, insufficientProducts: false, negativeSavings: false }));
        handleSubmit(true); // Ignore warnings when submitting bundle
    };

    const handleWarningCancel = () => {
        setWarnings((w) => ({ ...w, insufficientProducts: false, negativeSavings: false }));
    };

    // # of paid tix in bundle. Used to calculate per ticket fees
    const paidProductCount = cart.reduce((p, n) => {
        let count = p;

        if (n.price > 0) {
            count += n.quantity;
        }

        return count;
    }, 0);

    return (
        <>
            <Root>
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <Alert severity="info">
                            Create a bundle to offer a curated set of admission and add-on tickets
                            for your event, often at a discounted price. This feature makes it easy
                            for attendees to purchase multiple tickets in a single package,
                            enhancing their event experience and simplifying the process.
                        </Alert>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <Section>
                            <Header>About</Header>

                            <Content>
                                <TextField {...name.formProps} fullWidth label="Bundle Name" />

                                <TextArea label="Bundle Description" {...description.formProps} />

                                {productGroups.length > 0 && (
                                    <Dropdown
                                        {...productGroupId.formProps}
                                        label="Product Group"
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        <option value="">- No Category -</option>
                                        {productGroups.map((group) => (
                                            <option
                                                key={group.prod_group_id}
                                                value={group.prod_group_id}
                                            >
                                                {group.prod_group_name}
                                            </option>
                                        ))}
                                    </Dropdown>
                                )}
                            </Content>
                        </Section>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Section>
                            <ReceiptPreview
                                hostId={hostId}
                                eventId={eventId}
                                price={price.value}
                                name={name.value || 'Bundle'}
                                ticketCount={paidProductCount}
                            />
                        </Section>
                    </Grid>

                    <Grid item xs={12}>
                        <Section>
                            <Header>Build Bundle</Header>

                            <Typography>
                                Choose the items you'd like to include in your bundle for a combined
                                offering.
                            </Typography>

                            <Content>
                                <ProductSelect
                                    cart={cart}
                                    error={emptyCart}
                                    onUpdate={handleProductUpdate}
                                    totalValue={totalValue}
                                    ticketCount={ticketCount}
                                    savingAmount={savingAmount}
                                    savingPercent={savingPercent}
                                />
                            </Content>
                        </Section>
                    </Grid>

                    <Grid item xs={12}>
                        <Section>
                            <Header>Price and Availability</Header>

                            <Typography gutterBottom>
                                Specify the price and the maximum number of this bundle being made
                                available. Additionally, set the minimum and maximum limits for the
                                number of bundles that can be purchased per transaction.
                            </Typography>
                            <Typography variant="caption">
                                Note: once a ticket in a bundle sells out, the bundle itself won't
                                be available for sale.
                            </Typography>

                            <Content>
                                <Grid container spacing={2}>
                                    <Grid item xs={6} sm={6}>
                                        <PriceInput {...price.formProps} label="Price" />
                                    </Grid>
                                    <Grid item xs={6} sm={6} />
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            {...qtyAvailable.formProps}
                                            label="Qty Available"
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                        <TextField {...qtyMin.formProps} label="Order Minimum" />
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                        <TextField {...qtyMax.formProps} label="Order Maximum" />
                                    </Grid>
                                </Grid>
                                <Box mt={1}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={displayDiscountFlag}
                                                onChange={(e) =>
                                                    setDisplayDiscountFlag(e.target.checked)
                                                }
                                            />
                                        }
                                        label={`Show savings on store page (${savingPercent}%)`}
                                    />
                                </Box>
                            </Content>
                        </Section>
                    </Grid>

                    <Grid item xs={12}>
                        <Section>
                            <Header>Bundle Visibility</Header>

                            <Typography>
                                Select who can view and purchase this bundle online. Choose{' '}
                                <Bold>Available to Everyone</Bold> for public access,{' '}
                                <Bold>Not Available</Bold> to hide the bundle, or{' '}
                                <Bold>Access Code Required</Bold> to restrict access to with a
                                unique code.
                            </Typography>

                            <Box mt={2}>
                                <RadioGroup
                                    aria-label="audience"
                                    value={audience}
                                    onChange={handleAudienceChange}
                                >
                                    <FormControlLabel
                                        value="visible"
                                        control={<Radio />}
                                        label="Available to Everyone"
                                    />
                                    <FormControlLabel
                                        value="hidden"
                                        control={<Radio />}
                                        label="Not Available to Anyone"
                                    />
                                    <FormControlLabel
                                        value="accessCode"
                                        control={<Radio />}
                                        label="Access Code Required"
                                    />
                                </RadioGroup>

                                {audience === 'accessCode' && (
                                    <AccessCodeContainer>
                                        <Typography variant="caption">
                                            code has to be entered on the event page to view this
                                            bundle (max 50 characters)
                                        </Typography>
                                        <AccessCodeInput
                                            {...accessCode.formProps}
                                            variant="outlined"
                                            placeholder="Access Code"
                                        />
                                    </AccessCodeContainer>
                                )}
                            </Box>
                        </Section>
                    </Grid>

                    <Grid item xs={12}>
                        <Section>
                            <Header>When is this bundle available for purchase?</Header>

                            <Typography>
                                Set the time frame for when your bundle goes on sale and when it
                                stops.
                            </Typography>

                            <Content>
                                <Grid container spacing={2}>
                                    <Grid item xs={6} md={5}>
                                        <DatePicker
                                            {...saleStartDate.formProps}
                                            label="Start Date"
                                            error={
                                                saleStartDate.formProps.error || negativeSalePeriod
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={6} md={5}>
                                        <TimePicker
                                            {...saleStartTime.formProps}
                                            label="Start Time"
                                            error={
                                                saleStartTime.formProps.error || negativeSalePeriod
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={6} md={5}>
                                        <DatePicker
                                            {...saleEndDate.formProps}
                                            label="End Date"
                                            error={
                                                saleEndDate.formProps.error || negativeSalePeriod
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={6} md={5}>
                                        <TimePicker
                                            {...saleEndTime.formProps}
                                            label="End Time"
                                            error={
                                                saleEndTime.formProps.error || negativeSalePeriod
                                            }
                                        />
                                    </Grid>
                                </Grid>

                                {negativeSalePeriod && (
                                    <ErrorMessage>
                                        End date cannot come before start date
                                    </ErrorMessage>
                                )}

                                <DateSummary
                                    startDate={saleStartDate.value}
                                    startTime={saleStartTime.value}
                                    endDate={saleEndDate.value}
                                    endTime={saleEndTime.value}
                                />
                            </Content>
                        </Section>
                    </Grid>
                </Grid>

                <ButtonContainer>
                    {editMode && (
                        <>
                            {deletable ? (
                                <DeleteButton
                                    sx={{ marginRight: 'auto' }}
                                    onClick={onDelete}
                                    disabled={loading}
                                >
                                    Delete Bundle
                                </DeleteButton>
                            ) : (
                                <Tooltip message="Bundles cannot be deleted after being purchased on the store.">
                                    <span style={{ marginRight: 'auto' }}>
                                        <DeleteButton disabled>Delete Bundle</DeleteButton>
                                    </span>
                                </Tooltip>
                            )}
                        </>
                    )}
                    <CancelButton variant="outlined" disabled={loading} onClick={onCancel}>
                        Cancel
                    </CancelButton>
                    <SubmitButton variant="contained" disabled={loading} onClick={handleSubmit}>
                        {editMode ? 'Update' : 'Create'}
                    </SubmitButton>
                </ButtonContainer>

                <Box display="flex" flexDirection="column">
                    {d.createdAtMsg && (
                        <Typography align="right" variant="caption">
                            {d.createdAtMsg}
                        </Typography>
                    )}

                    {d.updatedAtMsg && (
                        <Typography align="right" variant="caption">
                            {d.updatedAtMsg}
                        </Typography>
                    )}
                </Box>
            </Root>

            <WarningConfirmation
                warnings={warnings}
                onConfirm={handleWarningAccept}
                onCancel={handleWarningCancel}
            />
        </>
    );
}

function WarningConfirmation(props) {
    const { warnings, onConfirm, onCancel } = props;

    const open = warnings.insufficientProducts || warnings.negativeSavings;

    return (
        <Dialog open={open} onClose={onCancel}>
            <DialogTitle>We found potential issues with this bundle</DialogTitle>
            <DialogContent>
                {warnings.insufficientProducts === true && (
                    <Box fontWeight="bold" mb={1}>
                        • Not enough tickets to fulfill this bundle. It will appear as sold out.
                    </Box>
                )}
                {warnings.negativeSavings === true && (
                    <Box fontWeight="bold" mb={1}>
                        • Cost of bundle is higher than total value of tickets.
                    </Box>
                )}
                <Box mt={4}>Are you sure you want to continue?</Box>
            </DialogContent>
            <DialogActions>
                <CancelButton onClick={onCancel}>Cancel</CancelButton>
                <SubmitButton onClick={onConfirm}>Yes</SubmitButton>
            </DialogActions>
        </Dialog>
    );
}

function ProductSelect(props) {
    const { cart, onUpdate, error, totalValue, ticketCount, savingAmount, savingPercent } = props;

    return (
        <>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <TableContainer component={Box}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Ticket</TableCell>
                                <TableCell align="right">Available</TableCell>
                                <TableCell align="right">Value</TableCell>
                                <TableCell align="center">Quantity</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {cart.map((item) => (
                                <PickerRow key={item.id}>
                                    <TableCell>
                                        <ProductDetails item={item} />
                                    </TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <Typography>{item.sold}/{item.total}</Typography>
                                    </TableCell>
                                    <TableCell width={60} align="right">
                                        <Typography>${item.price}</Typography>
                                    </TableCell>
                                    <TableCell width={130} align="right">
                                        <Box display="flex" alignItems="center">
                                            <IconButton
                                                color="secondary"
                                                onClick={() => onUpdate(item.id, item.quantity - 1)}
                                                size="large"
                                            >
                                                <RemoveIcon />
                                            </IconButton>
                                            <QuantityField
                                                error={error}
                                                value={item.quantity}
                                                onFocus={(e) => e.target.select()}
                                                onChange={(e) => onUpdate(item.id, e.target.value)}
                                            />
                                            <IconButton
                                                onClick={() => onUpdate(item.id, item.quantity + 1)}
                                                size="large"
                                            >
                                                <AddIcon />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </PickerRow>
                            ))}
                            <TableRow>
                                <TotalCell colSpan={2}>Total Value</TotalCell>
                                <TotalCell align="right">{formatPrice(totalValue)}</TotalCell>
                                <TotalCell align="center">{ticketCount} tickets</TotalCell>
                            </TableRow>
                            {savingAmount >= 0 ? (
                                <TableRow>
                                    <SavingCell colSpan={2}>Savings</SavingCell>
                                    <SavingCell align="right">
                                        {formatPrice(savingAmount)}
                                    </SavingCell>
                                    <SavingCell align="center">({savingPercent}% off)</SavingCell>
                                </TableRow>
                            ) : (
                                <TableRow>
                                    <NegativeSavingCell colSpan={2}>Savings</NegativeSavingCell>
                                    <NegativeSavingCell align="right">
                                        {formatPrice(savingAmount)}
                                    </NegativeSavingCell>
                                    <NegativeSavingCell align="center">
                                        ({savingPercent}% off)
                                    </NegativeSavingCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                {cart.map((item) => (
                    <Box key={item.id}>
                        <ProductDetails item={item} />
                        <Stack direction="row" spacing={3} mt={1.5}>
                            <Typography>
                                Available: {item.sold}/{item.total}
                            </Typography>
                            <Typography>Value: ${item.price}</Typography>
                        </Stack>

                        <Box display="flex" alignItems="center" mt={2}>
                            <IconButton
                                color="secondary"
                                onClick={() => onUpdate(item.id, item.quantity - 1)}
                                size="large"
                            >
                                <RemoveIcon fontSize="large" />
                            </IconButton>
                            <QuantityField
                                error={error}
                                value={item.quantity}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => onUpdate(item.id, e.target.value)}
                            />
                            <IconButton
                                color="secondary"
                                onClick={() => onUpdate(item.id, item.quantity + 1)}
                                size="large"
                            >
                                <AddIcon fontSize="large" />
                            </IconButton>
                        </Box>

                        <Box my={2}>
                            <Divider />
                        </Box>
                    </Box>
                ))}
                <TotalText>
                    Total Value: {formatPrice(totalValue)} ({ticketCount} tickets)
                </TotalText>
                {savingAmount >= 0 ? (
                    <SavingText>
                        Savings: {formatPrice(savingAmount)} ({savingPercent}% off)
                    </SavingText>
                ) : (
                    <NegativeSavingText>
                        Savings: {formatPrice(savingAmount)} ({savingPercent}% off)
                    </NegativeSavingText>
                )}
            </Box>
        </>
    );
}

function ProductDetails({ item }) {
    const typeLabel = item.type === 1 ? 'ADMISSION' : item.type === 2 ? 'ADD-ON' : null;

    return (
        <Box>
            <Stack direction="row" spacing={1.25} alignItems="center" useFlexGap flexWrap="wrap">
                <TicketName>{item.name}</TicketName>
                {typeLabel && <TypePill label={typeLabel} size="small" />}
            </Stack>
            <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap" mt={1.5}>
                <SaleStatusPill status={item.status} />
            </Stack>
        </Box>
    );
}

function SaleStatusPill({ status }) {
    const statuses = {
        on_sale: { label: 'On Sale', color: 'green' },
        sale_not_started: { label: 'Sale Not Started', color: 'blue' },
        sale_concluded: { label: 'Sale Ended', color: 'gray' },
        sold_out: { label: 'Sold Out', color: 'red' },
        hidden: { label: 'Hidden', color: 'gray' },
    };
    const saleStatus = statuses[status];

    if (!saleStatus) {
        return null;
    }

    return (
        <MetaPill icon={<AccessTimeIcon />} label={saleStatus.label} pillcolor={saleStatus.color} />
    );
}

function DateSummary(props) {
    const { startDate, startTime, endDate, endTime } = props;

    if (
        moment(startDate).isValid() === false ||
        moment(endDate).isValid() === false ||
        moment(startTime).isValid() === false ||
        moment(endTime).isValid() === false
    ) {
        return null;
    }

    const start = getMomentFromDateAndTime(startDate, startTime);
    const end = getMomentFromDateAndTime(endDate, endTime);
    const text = `${start.format('dddd, MMM Do @ h:mma')} and ${end.format('dddd, MMM Do @ h:mma')}`;

    return (
        <Box mt={2}>
            <DateSummaryText>This bundle can be purchased between:</DateSummaryText>
            {start > end ? (
                <DateSummaryTextError>{text}</DateSummaryTextError>
            ) : (
                <DateSummaryTextBold>{text}</DateSummaryTextBold>
            )}
        </Box>
    );
}

const Root = styled(Box)(({ theme }) => ({
    padding: theme.spacing(4),
    [theme.breakpoints.up('sm')]: {
        maxWidth: theme.breakpoints.values.lg,
        marginRight: 'auto',
        marginLeft: 'auto',
    },
    [theme.breakpoints.down('xl')]: {
        padding: theme.spacing(2),
    },
}));

const TicketName = styled(Typography)(({ theme }) => ({
    fontSize: 16,
    lineHeight: 1.25,
    fontWeight: theme.typography.fontWeightBold,
}));

const PickerRow = styled(TableRow)(({ theme }) => ({
    '& > .MuiTableCell-root': {
        padding: theme.spacing(2),
    },
}));

const TypePill = styled(Chip)(({ theme }) => ({
    height: 20,
    borderRadius: 6,
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[300]}`,
    '& .MuiChip-label': {
        fontSize: 12,
        fontWeight: theme.typography.fontWeightMedium,
        letterSpacing: '0.08em',
        color: theme.palette.grey[700],
    },
}));

const MetaPill = styled(Chip, {
    shouldForwardProp: (prop) => prop !== 'pillcolor',
})(({ theme, pillcolor }) => {
    const colors = {
        green: { backgroundColor: '#DDF5E6', color: '#0F5132' },
        blue: { backgroundColor: '#DCEEFF', color: '#0B4F86' },
        red: { backgroundColor: '#F9E8E4', color: '#B01212' },
        gray: { backgroundColor: theme.palette.grey[200], color: theme.palette.grey[700] },
    };

    return {
        height: 20,
        borderRadius: 12,
        ...colors[pillcolor],
        '& .MuiChip-icon': { color: 'inherit', fontSize: 12 },
        '& .MuiChip-label': {
            fontSize: 12,
            fontWeight: theme.typography.fontWeightMedium,
        },
    };
});

const QuantityField = styled(_TextField)(({ theme }) => ({
    width: 56,
}));
QuantityField.defaultProps = {
    variant: 'outlined',
    size: 'small',
    inputProps: {
        style: { textAlign: 'center' },
    },
};

const AddIcon = styled(AddCircleOutlineIcon)(({ theme }) => ({
    color: theme.palette.success.main,
}));

const RemoveIcon = styled(RemoveCircleOutlineIcon)(({ theme }) => ({
    color: theme.palette.error.main,
}));

const TextField = styled(_TextField)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));
TextField.defaultProps = { fullWidth: true, variant: 'outlined' };

const PriceInput = styled(_TextField)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));
PriceInput.defaultProps = {
    variant: 'outlined',
    fullWidth: true,
    InputProps: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
};

const ButtonContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
}));

const ErrorMessage = styled(Typography)(({ theme }) => ({
    color: theme.palette.error.main,
    marginTop: theme.spacing(2),
}));

const TotalText = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
}));

const SavingText = styled(Typography)(({ theme }) => ({
    color: theme.palette.success.dark,
}));

const NegativeSavingText = styled(Typography)(({ theme }) => ({
    color: theme.palette.error.dark,
}));

const TotalCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 'bold',
    fontSize: 20,
    paddingTop: theme.spacing(2),
    borderBottom: 0,
}));

const SavingCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 'bold',
    fontSize: 20,
    paddingTop: theme.spacing(2),
    borderBottom: 0,
    color: theme.palette.success.dark,
}));

const NegativeSavingCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 'bold',
    fontSize: 20,
    paddingTop: theme.spacing(2),
    borderBottom: 0,
    color: theme.palette.error.dark,
}));

const AccessCodeContainer = styled(Box)(({ theme }) => ({
    marginLeft: 32,
    display: 'flex',
    flexDirection: 'column',
}));

const AccessCodeInput = styled(_TextField)(({ theme }) => ({
    maxWidth: 400,
    marginTop: theme.spacing(1),
}));
AccessCodeInput.defaultProps = { size: 'small' };

const Bold = styled(Typography)(({ theme }) => ({
    display: 'inline',
    fontWeight: 'bold',
}));

const DateSummaryText = styled(Typography)(({ theme }) => ({
    color: theme.palette.info.dark,
}));

const DateSummaryTextBold = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    color: theme.palette.info.dark,
}));

const DateSummaryTextError = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    color: theme.palette.error.main,
}));
